import { runInAction, makeAutoObservable, toJS } from 'mobx';
import { INITIAL_SUBSCRIBER } from 'store/domain/electionStore';
import { INITIAL_GROUP_OBJECT } from 'store/domain/groupStore';
import { RootStore } from 'store/rootStore';
import { EnrollmentPeriod, EnrollmentStage } from 'types/enrollmentService';
import { Group } from 'types/groupService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { isEnrollmentPeriodNotEditable } from 'global/checklists-helpers/ChecklistHelper';
import {
  Subscriber,
  Choice,
  INITIAL_CHOICE,
  EmployeeInvitationStatusTypes,
  PersonInfo,
  ProductInfo,
  SubscriberTask,
} from 'types/electionService';
import { getGroupsByUserId } from 'api/operations/groupService';
import { getAllEnrollmentPeriodsByGroupId } from 'api/operations/enrollmentService';
import {
  getSubscriberWithChoices,
  getSubscriberByUserIdAndGroupId,
  removeSubscriberDependent,
  addOrUpdateDependent,
  getProducts,
  updateSubscriberInfo,
} from 'api/operations/electionService';
import { sortDependentsInSubscriber } from 'global/services-helpers/electionService';

export type WarningModalType = 'REMOVE_DEPENDENT' | 'FINISH_ENROLL | UPDATE_ENROLL';

// TODO refactor and transform to subscriber module.
export default class SubscriberStore {
  rootStore: RootStore;
  loading = false;
  currentSubscriber: Subscriber;
  periodId = '';
  activePeriods: EnrollmentPeriod[] = [];
  subIdInPeriod = '';
  groups: Group[] = [];
  activeGroup: Group = {
    ...INITIAL_GROUP_OBJECT,
  };

  isPeriodNotEditable = true;
  isSubscriberElected = false;

  public get subscriberId(): string {
    return this.currentSubscriber.id;
  }

  public get subscriberTasks(): { active: SubscriberTask[]; inactive: SubscriberTask[] } {
    return {
      active: this.currentSubscriber.tasks.filter((task) => task.active),
      inactive: this.currentSubscriber.tasks.filter((task) => !task.active),
    };
  }

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private setActivePeriod(period: EnrollmentPeriod[]): void {
    this.activePeriods = period;
  }

  private setSubIdInPeriod(subId: string): void {
    this.subIdInPeriod = subId;
  }

  private setIsSubscriberWithElectedStatus(status: boolean): void {
    this.isSubscriberElected = status;
  }

  private updatePeriodEditableStatus(status: boolean): void {
    this.isPeriodNotEditable = status;
  }

  public async addDependent(dependent: PersonInfo): Promise<void> {
    const res = await addOrUpdateDependent(this.currentSubscriber.id, dependent);
    // endpoint return only new dependent id
    const id = res.data?.addOrUpdateDependent;
    runInAction(() => {
      if (!id) return;
      this.currentSubscriber.custom.dependents.push({ ...dependent, id });
    });
  }

  public async editDependent(dependent: PersonInfo): Promise<void> {
    await addOrUpdateDependent(this.currentSubscriber.id, dependent);
    runInAction(() => {
      const updatedDependents = this.currentSubscriber.custom.dependents.map((dependentInfo): PersonInfo => {
        if (dependent.id === dependentInfo.id) return dependent;
        return { ...dependentInfo };
      });
      this.currentSubscriber.custom.dependents = updatedDependents;
    });
  }

  public removeDependent(dependentId: string): void {
    void removeSubscriberDependent(this.currentSubscriber.id, dependentId);

    // Remove dependent from general(custom) dependents list
    const updatedSubscriberDependents =
      this.currentSubscriber.custom.dependents.filter(({ id }) => dependentId !== id) || [];

    // Remove dependent from choice if subscriber removed it from dependents
    const updatedChoices = this.currentSubscriber.choice.map((choice) => {
      if (choice.dependentIds.includes(dependentId)) {
        const updatedDependentsList = choice.dependentIds.filter((id) => dependentId !== id);
        return { ...choice, dependentIds: updatedDependentsList };
      }
      return choice;
    });

    this.currentSubscriber = {
      ...this.currentSubscriber,
      choice: updatedChoices,
      custom: { dependents: updatedSubscriberDependents },
    };
  }

  async updatePrimary(periodId: string, answerValues: Record<string, string>): Promise<void> {
    this.currentSubscriber.common = { ...this.currentSubscriber.common, ...answerValues };
    await updateSubscriberInfo(periodId, this.currentSubscriber);
  }

  public async setCurrentSubscriber(subscriberId: string): Promise<void> {
    this.setLoading(true);
    const subscriberInfo = await getSubscriberWithChoices(subscriberId, this.periodId);
    await runInAction(async () => {
      const sortedSubscriberInfo = sortDependentsInSubscriber(subscriberInfo);
      const products = await getProducts(this.periodId);

      // if there are no choices in subscriber make all dependents selected by default
      if (sortedSubscriberInfo.choice.length !== products?.length && products?.length) {
        sortedSubscriberInfo.choice = this.createInitialChoicesList(products);
      }
      this.currentSubscriber = sortedSubscriberInfo;

      this.setLoading(false);
    });
  }

  public resetCurrentSubscriber(): void {
    this.currentSubscriber = INITIAL_SUBSCRIBER;
  }

  private createInitialChoicesList(products: ProductInfo[]): Choice[] {
    const defaultDependentsList: string[] = this.currentSubscriber.custom.dependents.map(({ id }) => id) || [];

    const defaultInitialChoicesList: Choice[] =
      products.map(({ productLineId }) => {
        return { ...INITIAL_CHOICE, productLineId, dependentIds: defaultDependentsList };
      }) || [];

    return defaultInitialChoicesList;
  }

  // eslint-disable-next-line class-methods-use-this
  public getLinkToPortfolioDetails(
    currentSubscriber: Subscriber | null,
    productLineId: ProductLineId,
    products: ProductInfo[],
    cartId: string,
    periodId: string,
  ): string {
    const currentProductLinePortfolioId =
      products.find((product) => product.productLineId === productLineId)?.portfolioId || '';

    if (!currentProductLinePortfolioId) {
      throw new Error('Current product line portfolio id is not found');
    }

    return `/product-compare?type=available-products&portfolioIds=${currentProductLinePortfolioId}&cartId=${cartId}&productLineId=${productLineId}&periodId=${periodId}&eeClass=${
      currentSubscriber?.employeeClass || ''
    }`;
  }

  public setPeriodEditableStatus(enrollmentPeriod: EnrollmentPeriod): void {
    this.periodId = enrollmentPeriod.id;
    this.updatePeriodEditableStatus(isEnrollmentPeriodNotEditable(enrollmentPeriod.stage));
  }

  private async checkSubscriberElectedStatus(): Promise<void> {
    const subscriberInfo = await getSubscriberWithChoices(this.subIdInPeriod, this.periodId);
    runInAction(() => {
      const subStatus = subscriberInfo?.selfEnrollStatus;
      this.setIsSubscriberWithElectedStatus(subStatus === EmployeeInvitationStatusTypes.ELECTED);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  public getCurrentChoice(subscriber: Subscriber, productLineId: string): Choice | undefined {
    const currentChoice = subscriber?.choice.find((choiceItem) => choiceItem.productLineId === productLineId);

    return currentChoice;
  }

  // For employee
  // TODO move this specific logic to employee/employer profile store
  private async getActivePeriod(groupId: string): Promise<void> {
    this.setLoading(true);

    const result = await getAllEnrollmentPeriodsByGroupId(groupId);
    await runInAction(async () => {
      const enrollsWithSubsResult = await this.rootStore.electionStore.getEnrollsByUserId();

      runInAction(() => {
        const enrollsWithSubs = enrollsWithSubsResult.data.getEnrollsByUserId;

        const periods = result
          .map((enrollmentPeriod) => toJS(enrollmentPeriod))
          .slice()
          .filter((enrollmentPeriod) => enrollmentPeriod.hasConfirmedProposal)
          .filter((enrollmentPeriod) => enrollmentPeriod.stage === EnrollmentStage.ENROLLING);

        if (periods.length > 1) throw new Error('There can be only one active period');

        const isPeriodActive = enrollsWithSubs ? Object.keys(enrollsWithSubs).includes(periods[0]?.id) : false;
        // enrollment period should be in the array for the logic of the enrollment card common component
        if (isPeriodActive) {
          this.setActivePeriod([periods[0]]);
          this.setSubIdInPeriod(enrollsWithSubs[periods[0].id]);
          // for showing different tag on the enrollment card
          void this.checkSubscriberElectedStatus();
        } else {
          // don't show enrollment card if it's inactive
          this.setActivePeriod([]);
        }
      });
    });
    this.setLoading(false);
  }

  // TODO after employee profile redesign move this specific logic to employee/employer profile store
  /**
   * Sets `activeGroup` when `groupId` exists in `this.groups`. Does not load period/subscriber.
   */
  private assignActiveGroupById(groupId: string): boolean {
    const currentGroup = this.groups.find(({ id }) => id === groupId);
    if (currentGroup) {
      this.activeGroup = currentGroup;
      return true;
    }
    return false;
  }

  public async getGroupsList(currentGroupId = ''): Promise<void> {
    this.setLoading(true);

    const groups = await getGroupsByUserId();
    await runInAction(async () => {
      this.groups = groups;

      if (!this.activeGroup.id) {
        this.assignActiveGroupById(currentGroupId || this.groups[0]?.id || '');
      }

      if (this.activeGroup.id) {
        await this.getActivePeriod(this.activeGroup.id);
        await this.getSubscriberByUserIdAndGroupId(this.activeGroup.id);
      }
      this.setLoading(false);
    });
  }

  public async getSubscriberByUserIdAndGroupId(groupId: string): Promise<void> {
    const subscriber = await getSubscriberByUserIdAndGroupId(groupId);
    runInAction(() => {
      this.currentSubscriber = subscriber || INITIAL_SUBSCRIBER;
    });
  }

  /**
   * Called when the user picks another group in the profile group tabs.
   * Updates `activeGroup` and reloads enrollment period + subscriber (tasks, etc.) for that group.
   */
  public switchActiveGroupTabAndRefetch(groupId: string): void {
    if (!this.assignActiveGroupById(groupId)) return;
    void this.getActivePeriod(this.activeGroup.id);
    void this.getSubscriberByUserIdAndGroupId(this.activeGroup.id);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.currentSubscriber = INITIAL_SUBSCRIBER;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
